
import React, { useState, useEffect } from 'react';
import { IconFlow, IconServer, IconPlay, IconPause, IconSparkles, IconQuant } from '../components/Icons';
import { optimizeWorkflow } from '../services/geminiService';
import toast from 'react-hot-toast';

interface Task {
    id: string;
    name: string;
    status: 'Waiting' | 'Running' | 'Completed' | 'Failed';
    progress: number; // 0-100
    duration: string;
    logs: string[];
}

const INITIAL_TASKS: Task[] = [
    { id: 'T1', name: 'Load Market Data (Fixings)', status: 'Completed', progress: 100, duration: '2m 15s', logs: ['Fetching Rates...', 'Fetching FX...', 'Done.'] },
    { id: 'T2', name: 'Construct Yield Curves', status: 'Completed', progress: 100, duration: '4m 30s', logs: ['Bootstrapping USD OIS...', 'Done.'] },
    { id: 'T3', name: 'Calibrate Vol Surfaces', status: 'Running', progress: 45, duration: 'Running...', logs: ['Fitting SABR Model...', 'Optimizing Parameters...'] },
    { id: 'T4', name: 'Portfolio Pricing (MtM)', status: 'Waiting', progress: 0, duration: '--', logs: [] },
    { id: 'T5', name: 'Calculate VaR (Monte Carlo)', status: 'Waiting', progress: 0, duration: '--', logs: [] },
    { id: 'T6', name: 'Generate Regulatory Reports', status: 'Waiting', progress: 0, duration: '--', logs: [] },
];

const WorkflowView: React.FC = () => {
    const [tasks, setTasks] = useState<Task[]>(INITIAL_TASKS);
    const [isRunning, setIsRunning] = useState(true);
    const [aiOptimization, setAiOptimization] = useState('');
    const [isOptimizing, setIsOptimizing] = useState(false);

    // Simulate Task Progress
    useEffect(() => {
        if (!isRunning) return;

        const interval = setInterval(() => {
            setTasks(prev => {
                const newTasks = [...prev];
                const runningIdx = newTasks.findIndex(t => t.status === 'Running');
                
                if (runningIdx !== -1) {
                    const task = newTasks[runningIdx];
                    if (task.progress < 100) {
                        task.progress += 5; // Increment progress
                        if (Math.random() > 0.8) task.logs.push(`Processing batch ${Math.floor(task.progress)}%...`);
                    } else {
                        task.status = 'Completed';
                        task.duration = '5m 12s'; // Mock completion time
                        task.logs.push('Task completed successfully.');
                        
                        // Start next
                        if (runningIdx + 1 < newTasks.length) {
                            newTasks[runningIdx + 1].status = 'Running';
                        }
                    }
                }
                return newTasks;
            });
        }, 800);

        return () => clearInterval(interval);
    }, [isRunning]);

    const handlePause = () => {
        setIsRunning(!isRunning);
        toast(isRunning ? "Workflow Paused" : "Workflow Resumed", { icon: isRunning ? '⏸️' : '▶️' });
    };

    const handleOptimize = async () => {
        setIsOptimizing(true);
        const advice = await optimizeWorkflow(tasks);
        setAiOptimization(advice);
        setIsOptimizing(false);
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Control Panel */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto custom-scrollbar">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                    <IconFlow /> AiQ Orchestrator
                </h2>
                <p className="text-xs text-slate-500 mb-6">End-of-Day Batch Monitor</p>

                <div className="space-y-6">
                    <div className="bg-slate-50 p-4 rounded border border-slate-200">
                        <h3 className="text-xs font-bold text-slate-700 uppercase mb-2">Batch Control</h3>
                        <div className="flex gap-2">
                            <button 
                                onClick={handlePause}
                                className={`flex-1 py-2 rounded font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                                    isRunning 
                                    ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100' 
                                    : 'bg-blue-600 text-white shadow-md hover:bg-blue-700'
                                }`}
                            >
                                {isRunning ? <><IconPause /> Pause</> : <><IconPlay /> Resume</>}
                            </button>
                        </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded border border-slate-200">
                        <h3 className="text-xs font-bold text-slate-700 uppercase mb-2">Compute Status</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between text-xs text-slate-600">
                                <span>CPU Load</span>
                                <span className="font-mono font-bold">84%</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-blue-500 h-full" style={{ width: '84%' }}></div>
                            </div>
                            
                            <div className="flex justify-between text-xs text-slate-600 mt-2">
                                <span>Memory Usage</span>
                                <span className="font-mono font-bold">62%</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-purple-500 h-full" style={{ width: '62%' }}></div>
                            </div>
                        </div>
                    </div>

                    <button 
                        onClick={handleOptimize}
                        disabled={isOptimizing}
                        className="w-full py-3 bg-slate-800 text-white rounded font-bold text-sm shadow flex items-center justify-center gap-2 hover:bg-slate-900 transition-all"
                    >
                        {isOptimizing ? <span className="animate-spin">⏳</span> : <IconSparkles />}
                        Analyze Critical Path
                    </button>

                    {aiOptimization && (
                        <div className="bg-blue-50 border border-blue-100 p-3 rounded text-sm text-blue-900 leading-relaxed animate-fade-in">
                            <div className="font-bold text-xs uppercase mb-1 flex items-center gap-1"><IconQuant /> Suggestion</div>
                            {aiOptimization}
                        </div>
                    )}
                </div>
            </div>

            {/* Right: Visual Pipeline */}
            <div className="flex-1 p-8 overflow-y-auto custom-scrollbar relative">
                <div className="max-w-3xl mx-auto space-y-4 relative">
                    {/* Connecting Line */}
                    <div className="absolute left-8 top-4 bottom-4 w-0.5 bg-slate-200 -z-10"></div>

                    {tasks.map((task, index) => (
                        <div key={task.id} className="flex items-start gap-6 relative animate-scale-up" style={{ animationDelay: `${index * 0.1}s` }}>
                            {/* Status Icon */}
                            <div className={`w-16 h-16 rounded-full shrink-0 flex items-center justify-center border-4 z-10 bg-white shadow-sm ${
                                task.status === 'Completed' ? 'border-emerald-500 text-emerald-500' :
                                task.status === 'Running' ? 'border-blue-500 text-blue-500 animate-pulse' :
                                'border-slate-200 text-slate-300'
                            }`}>
                                {task.status === 'Completed' && <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                {task.status === 'Running' && <IconServer />}
                                {task.status === 'Waiting' && <span className="font-bold text-sm text-slate-400">#{index+1}</span>}
                            </div>

                            {/* Task Card */}
                            <div className={`flex-1 bg-white p-5 rounded-lg border shadow-sm transition-all ${
                                task.status === 'Running' ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200'
                            }`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h4 className={`font-bold ${task.status === 'Waiting' ? 'text-slate-400' : 'text-slate-800'}`}>{task.name}</h4>
                                        <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                                            task.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                                            task.status === 'Running' ? 'bg-blue-100 text-blue-700' :
                                            'bg-slate-100 text-slate-500'
                                        }`}>
                                            {task.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-slate-400 uppercase font-bold">Duration</div>
                                        <div className="text-sm font-mono text-slate-600">{task.duration}</div>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                {task.status !== 'Waiting' && (
                                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mb-3">
                                        <div 
                                            className={`h-full transition-all duration-500 ${task.status === 'Completed' ? 'bg-emerald-500' : 'bg-blue-500'}`} 
                                            style={{ width: `${task.progress}%` }}
                                        ></div>
                                    </div>
                                )}

                                {/* Logs */}
                                {task.logs.length > 0 && (
                                    <div className="bg-slate-900 text-green-400 p-2 rounded text-[10px] font-mono max-h-20 overflow-y-auto custom-scrollbar">
                                        {task.logs.map((log, i) => (
                                            <div key={i}>&gt {log}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WorkflowView;
